import React, { useState } from 'react';
import { Image } from 'antd';

import './index.less';

import {
  AgentMessageEventToolCallDataInput,
  AgentMessageEventToolCallStatus,
  AgentToolCallData,
  DataInterfaceType,
  ELMA365ReadFileToolCallDataToolTypeEnum,
  ELMA365GeneralToolCallDataInput,
  ELMA365ToolkitToolCallData,
  FileReadFileToolDataToolTypeEnum,
  FileToolCallDataInput,
  FileWriteFileToolDataToolTypeEnum,
  SmartSearchToolCallData,
  ToolsetSettingsType,
  WebToolCallData,
  FileDescribeImageToolDataToolTypeEnum,
  ELMA365FetchAppFeedToolCallDataToolTypeEnum,
  ELMA365DescribeImageToolCallDataToolTypeEnum,
  SmartSearchDownloadDocumentToolCallDataToolTypeEnum,
  SmartSearchQueryToolCallDataToolTypeEnum,
  SmartSearchSearchDocumentsToolCallDataToolTypeEnum,
} from '../../../../gamma-api';
import IbIcon from '../../../components/common/IbIcon';
import IbTypography from '../../../components/common/IbTypography';
import IbCollapse, { IbCollapsePanel } from '../../../components/common/IbCollapse';
import MarkdownContent from '../../../components/MarkdownContent';
import IbUrl from '../../../components/common/IbUrl';
import Switcher from '../../../components/common/Switcher';
import IbDivider from '../../../components/common/IbDivider';
import { getTranslateFunc } from '../../../utils/stringUtil';
import { agentMessageEventDataToMarkdown } from '../utils';

import { showRenderModeSwitcher } from './utils';

const MAIN_CLASS_NAME = 'tool-call-event-details';
const INPUT_PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__input-panel`;
const OUTPUT_PANEL_CLASS_NAME = `${MAIN_CLASS_NAME}__output-panel`;
const INPUT_BLOCK_CLASS_NAME = `${INPUT_PANEL_CLASS_NAME}__block`;
const INPUT_ROW_CLASS_NAME = `${INPUT_BLOCK_CLASS_NAME}__row`;
const OUTPUT_BLOCK_CLASS_NAME = `${OUTPUT_PANEL_CLASS_NAME}__block`;
const OUTPUT_ROW_CLASS_NAME = `${OUTPUT_BLOCK_CLASS_NAME}__row`;

const TRANSLATE_KEY = 'Agent: Tool call event details:';

export enum DetailsRenderMode {
  RAW = 'Raw',
  STRUCTURED = 'Structured',
}

export interface IToolCallEventDetailsProps {
  toolCallData: AgentMessageEventToolCallDataInput;
}

const ToolCallEventDetails: React.FC<IToolCallEventDetailsProps> = ({ toolCallData }) => {
  const translate = getTranslateFunc(TRANSLATE_KEY);
  const translateFile = getTranslateFunc(`${TRANSLATE_KEY} File:`);
  const translateAgent = getTranslateFunc(`${TRANSLATE_KEY} Agent:`);
  const translateSmartSearch = getTranslateFunc(`${TRANSLATE_KEY} Smart search:`);
  const translateFetchUrl = getTranslateFunc(`${TRANSLATE_KEY} Fetch URL:`);
  const translateElma365General = getTranslateFunc(`${TRANSLATE_KEY} ELMA365 General:`);

  const [renderMode, setRenderMode] = useState(DetailsRenderMode.STRUCTURED);

  const isRawMode = renderMode === DetailsRenderMode.RAW;

  const onRenderModeChange = (value: string) => {
    setRenderMode(value as DetailsRenderMode);
  };

  const renderMCPServerInput = () => (
    <MarkdownContent>{agentMessageEventDataToMarkdown(toolCallData.arguments)}</MarkdownContent>
  );

  const renderSmartSearchInput = () => {
    const metadata = toolCallData.metadata.data as SmartSearchToolCallData;
    const fullData = { ...toolCallData.arguments };

    if (metadata.tool_data.tool_type === SmartSearchDownloadDocumentToolCallDataToolTypeEnum.DownloadDocument) {
      fullData.max_content_token_count = metadata.tool_data.max_content_token_count;
    }

    if (isRawMode) {
      return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
    }

    switch (metadata.tool_data.tool_type) {
      case SmartSearchQueryToolCallDataToolTypeEnum.Query:
        return (
          <div className={INPUT_BLOCK_CLASS_NAME}>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{toolCallData.arguments.query}</IbTypography.Paragraph>
            </div>
          </div>
        );
      case SmartSearchSearchDocumentsToolCallDataToolTypeEnum.SearchDocuments:
        return (
          <div className={INPUT_BLOCK_CLASS_NAME}>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph type="secondary">{toolCallData.arguments.search_text}</IbTypography.Paragraph>
            </div>
          </div>
        );
      default:
        break;
    }

    return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
  };

  const renderDateTimeInput = () => translate('Empty');

  const renderWebInput = () => {
    const metadata = toolCallData.metadata.data as WebToolCallData;

    const fullData = {
      ...toolCallData.arguments,
      max_content_token_count: metadata.tool_data.max_content_token_count,
      fetch_timeout_seconds: metadata.tool_data.fetch_timeout_seconds,
      allowed_hosts: metadata.tool_data.allowed_hosts,
    };

    return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
  };

  const renderFileInput = () => {
    const metadata = toolCallData.metadata.data as FileToolCallDataInput;

    switch (metadata.tool_data.tool_type) {
      case FileWriteFileToolDataToolTypeEnum.WriteFile: {
        if (isRawMode) {
          return <MarkdownContent>{agentMessageEventDataToMarkdown(toolCallData.arguments)}</MarkdownContent>;
        }
        return (
          <div className={INPUT_BLOCK_CLASS_NAME}>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Write file: File name')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">{toolCallData.arguments.file_name}</IbTypography.Paragraph>
            </div>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Write file: Mime type')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">{toolCallData.arguments.mime_type}</IbTypography.Paragraph>
            </div>
            <IbTypography.Paragraph disabled strong type="secondary">
              {translateFile('Write file: Content')}
            </IbTypography.Paragraph>
            <MarkdownContent>{toolCallData.arguments.content as string}</MarkdownContent>
          </div>
        );
      }
      case FileReadFileToolDataToolTypeEnum.ReadFile: {
        const fullData = {
          ...toolCallData.arguments,
          max_content_token_count: metadata.tool_data.max_content_token_count,
        };
        if (isRawMode) {
          return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
        }
        return (
          <div className={INPUT_BLOCK_CLASS_NAME}>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Read file: Url')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">
                <IbUrl url={toolCallData.arguments.url} />
              </IbTypography.Paragraph>
            </div>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Read file: Offset')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">
                <IbUrl url={toolCallData.arguments.offset} />
              </IbTypography.Paragraph>
            </div>
          </div>
        );
      }
      case FileDescribeImageToolDataToolTypeEnum.DescribeImage: {
        const fullData = {
          ...toolCallData.arguments,
          llm_settings: metadata.tool_data.llm_settings,
          default_prompt: metadata.tool_data.default_prompt,
          allow_prompt_override: metadata.tool_data.allow_prompt_override,
        };
        if (isRawMode) {
          return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
        }

        const promptUsed =
          metadata.tool_data.allow_prompt_override && toolCallData.arguments.prompt
            ? toolCallData.arguments.prompt
            : metadata.tool_data.default_prompt;

        return (
          <div className={INPUT_BLOCK_CLASS_NAME}>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Describe image: URL')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">
                <IbUrl url={toolCallData.arguments.url} />
              </IbTypography.Paragraph>
            </div>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Describe image: Prompt')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">{promptUsed}</IbTypography.Paragraph>
            </div>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Describe image: LLM settings')}
              </IbTypography.Paragraph>
              <MarkdownContent>{agentMessageEventDataToMarkdown(metadata.tool_data.llm_settings)}</MarkdownContent>
            </div>
            <div className={INPUT_ROW_CLASS_NAME}>
              <Image src={toolCallData.arguments.url} />
            </div>
          </div>
        );
      }
    }
  };

  const renderELMA365GeneralInput = () => {
    const metadata = toolCallData.metadata.data as ELMA365GeneralToolCallDataInput;

    const fullData: Record<string, unknown> = {
      ...toolCallData.arguments,
      base_url: metadata.base_url,
      x_token: metadata.x_token,
    };

    if (
      metadata.tool_data.tool_type === ELMA365ReadFileToolCallDataToolTypeEnum.ReadFile ||
      metadata.tool_data.tool_type === ELMA365FetchAppFeedToolCallDataToolTypeEnum.FetchAppFeed
    ) {
      fullData.max_content_token_count = metadata.tool_data.max_content_token_count;
    }

    if (metadata.tool_data.tool_type === ELMA365DescribeImageToolCallDataToolTypeEnum.DescribeImage) {
      fullData.llm_settings = metadata.tool_data.llm_settings;
      fullData.default_prompt = metadata.tool_data.default_prompt;
      fullData.allow_prompt_override = metadata.tool_data.allow_prompt_override;
    }

    return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
  };

  const renderELMA365ToolkitInput = () => {
    const metadata = toolCallData.metadata.data as ELMA365ToolkitToolCallData;

    const fullData: Record<string, unknown> = {
      ...toolCallData.arguments,
      base_url: metadata.base_url,
      x_token: metadata.x_token,
    };

    return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
  };

  const renderAgentInput = () => {
    const metadata = toolCallData.metadata.data as AgentToolCallData;
    if (isRawMode) {
      const fullData: Record<string, unknown> = {
        arguments: toolCallData.arguments,
        metadata: {
          tool_name: toolCallData.name,
          ...metadata,
        },
      };
      return <MarkdownContent>{agentMessageEventDataToMarkdown(fullData)}</MarkdownContent>;
    }

    return (
      <div className={INPUT_BLOCK_CLASS_NAME}>
        <div className={INPUT_ROW_CLASS_NAME}>
          <IbTypography.Paragraph disabled strong type="secondary">
            {translateAgent('Agent name')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{metadata.agent_name}</IbTypography.Paragraph>
        </div>
        <div className={INPUT_ROW_CLASS_NAME}>
          <IbTypography.Paragraph disabled strong type="secondary">
            {translateAgent('Tool description')}
          </IbTypography.Paragraph>
          <IbTypography.Paragraph type="secondary">{metadata.tool_description}</IbTypography.Paragraph>
        </div>
        {metadata.agent_data_interface_type == DataInterfaceType.Chat ? (
          <>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateAgent('Query')}
              </IbTypography.Paragraph>
              <IbTypography.Paragraph type="secondary">{toolCallData.arguments.query}</IbTypography.Paragraph>
            </div>
          </>
        ) : (
          <>
            <div className={INPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateAgent('Arguments')}
              </IbTypography.Paragraph>
            </div>
            <IbDivider type="horizontal" />
            <div className={INPUT_ROW_CLASS_NAME}>
              <MarkdownContent>{agentMessageEventDataToMarkdown(toolCallData.arguments)}</MarkdownContent>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderInput = () => {
    switch (toolCallData.metadata.toolset_settings_type) {
      case ToolsetSettingsType.McpServer:
        return renderMCPServerInput();
      case ToolsetSettingsType.SmartSearch:
        return renderSmartSearchInput();
      case ToolsetSettingsType.DateTime:
        return renderDateTimeInput();
      case ToolsetSettingsType.Web:
        return renderWebInput();
      case ToolsetSettingsType.File:
        return renderFileInput();
      case ToolsetSettingsType.Elma365General:
        return renderELMA365GeneralInput();
      case ToolsetSettingsType.Elma365Toolkit:
        return renderELMA365ToolkitInput();
      case ToolsetSettingsType.Agent:
        return renderAgentInput();
      default:
        return <MarkdownContent>{agentMessageEventDataToMarkdown(toolCallData.arguments)}</MarkdownContent>;
    }
  };

  const renderOutput = () => {
    if (isRawMode || toolCallData.status !== AgentMessageEventToolCallStatus.Success) {
      return <MarkdownContent>{agentMessageEventDataToMarkdown(toolCallData.output, isRawMode)}</MarkdownContent>;
    }
    switch (toolCallData.metadata.toolset_settings_type) {
      case ToolsetSettingsType.SmartSearch: {
        const metadata = toolCallData.metadata.data as SmartSearchToolCallData;
        switch (metadata.tool_data.tool_type) {
          case SmartSearchQueryToolCallDataToolTypeEnum.Query: {
            return <MarkdownContent>{toolCallData.output?.response as string}</MarkdownContent>;
          }
          case SmartSearchDownloadDocumentToolCallDataToolTypeEnum.DownloadDocument: {
            const output = toolCallData.output as { [key: string]: unknown };
            return (
              <div className={OUTPUT_BLOCK_CLASS_NAME}>
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateSmartSearch('Download document: Chunk content length')}
                  </IbTypography.Paragraph>
                  {output.chunk_content_length}
                </div>
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateSmartSearch('Download document: Total content length')}
                  </IbTypography.Paragraph>
                  {output.total_content_length}
                </div>
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateSmartSearch('Download document: Has more content')}
                  </IbTypography.Paragraph>
                  {output.has_more_content
                    ? translateSmartSearch('Download document: Has more content: Yes')
                    : translateSmartSearch('Download document: Has more content: No')}
                </div>
                {output.has_more_content ? (
                  <div className={OUTPUT_ROW_CLASS_NAME}>
                    <IbTypography.Paragraph disabled strong type="secondary">
                      {translateSmartSearch('Download document: Next chunk offset')}
                    </IbTypography.Paragraph>
                    {output.next_chunk_offset}
                  </div>
                ) : null}
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateSmartSearch('Download document: Content')}
                </IbTypography.Paragraph>
                <MarkdownContent>{output.content as string}</MarkdownContent>
              </div>
            );
          }
        }
        break;
      }
      case ToolsetSettingsType.Web: {
        const output = toolCallData.output as Record<string, unknown>;
        if (output.is_error) {
          return (
            <div className={OUTPUT_BLOCK_CLASS_NAME}>
              <div className={OUTPUT_ROW_CLASS_NAME}>
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateFetchUrl('Status code')}
                </IbTypography.Paragraph>
                {output.status_code}
              </div>
              <IbTypography.Paragraph type="secondary">{output.error_message}</IbTypography.Paragraph>
            </div>
          );
        }

        return (
          <div className={OUTPUT_BLOCK_CLASS_NAME}>
            <div className={OUTPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFetchUrl('Chunk content length')}
              </IbTypography.Paragraph>
              {output.chunk_content_length}
            </div>
            <div className={OUTPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFetchUrl('Total content length')}
              </IbTypography.Paragraph>
              {output.total_content_length}
            </div>
            <div className={OUTPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFetchUrl('Has more content')}
              </IbTypography.Paragraph>
              {output.has_more_content
                ? translateFetchUrl('Has more content: Yes')
                : translateFetchUrl('Has more content: No')}
            </div>
            {output.has_more_content ? (
              <div className={OUTPUT_ROW_CLASS_NAME}>
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateFetchUrl('Next chunk offset')}
                </IbTypography.Paragraph>
                {output.next_chunk_offset}
              </div>
            ) : null}
            <div className={OUTPUT_ROW_CLASS_NAME}>
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFetchUrl('Content')}
              </IbTypography.Paragraph>
            </div>
            <MarkdownContent>{output.content as string}</MarkdownContent>
          </div>
        );
      }
      case ToolsetSettingsType.File: {
        const metadata = toolCallData.metadata.data as FileToolCallDataInput;
        if (metadata.tool_data.tool_type === FileReadFileToolDataToolTypeEnum.ReadFile) {
          const output = toolCallData.output as Record<string, unknown>;
          return (
            <div className={OUTPUT_BLOCK_CLASS_NAME}>
              <div className={OUTPUT_ROW_CLASS_NAME}>
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateFile('Read file: Chunk content length')}
                </IbTypography.Paragraph>
                {output.chunk_content_length}
              </div>
              <div className={OUTPUT_ROW_CLASS_NAME}>
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateFile('Read file: Total content length')}
                </IbTypography.Paragraph>
                {output.total_content_length}
              </div>
              <div className={OUTPUT_ROW_CLASS_NAME}>
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateFile('Read file: Mime type')}
                </IbTypography.Paragraph>
                {output.mime_type}
              </div>
              <div className={OUTPUT_ROW_CLASS_NAME}>
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateFile('Read file: Has more content')}
                </IbTypography.Paragraph>
                {output.has_more_content
                  ? translateFile('Read file: Has more content: Yes')
                  : translateFile('Read file: Has more content: No')}
              </div>
              {output.has_more_content ? (
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateFile('Read file: Next chunk offset')}
                  </IbTypography.Paragraph>
                  {output.next_chunk_offset}
                </div>
              ) : null}
              <IbTypography.Paragraph disabled strong type="secondary">
                {translateFile('Read file: Content')}
              </IbTypography.Paragraph>
              <MarkdownContent>{output.content as string}</MarkdownContent>
            </div>
          );
        }
        if (metadata.tool_data.tool_type === FileDescribeImageToolDataToolTypeEnum.DescribeImage) {
          const output = toolCallData.output as Record<string, unknown>;
          if (output?.result) {
            return <MarkdownContent>{output.result as string}</MarkdownContent>;
          }
        }
        break;
      }
      case ToolsetSettingsType.Elma365General: {
        const metadata = toolCallData.metadata.data as ELMA365GeneralToolCallDataInput;
        switch (metadata.tool_data.tool_type) {
          case ELMA365ReadFileToolCallDataToolTypeEnum.ReadFile: {
            const output = toolCallData.output as { [key: string]: unknown };
            return (
              <div className={OUTPUT_BLOCK_CLASS_NAME}>
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateElma365General('Read file: Chunk content length')}
                  </IbTypography.Paragraph>
                  {output.chunk_content_length}
                </div>
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateElma365General('Read file: Total content length')}
                  </IbTypography.Paragraph>
                  {output.total_content_length}
                </div>
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateElma365General('Read file: Mime type')}
                  </IbTypography.Paragraph>
                  {output.mime_type}
                </div>
                <div className={OUTPUT_ROW_CLASS_NAME}>
                  <IbTypography.Paragraph disabled strong type="secondary">
                    {translateElma365General('Read file: Has more content')}
                  </IbTypography.Paragraph>
                  {output.has_more_content
                    ? translateElma365General('Read file: Has more content: Yes')
                    : translateElma365General('Read file: Has more content: No')}
                </div>
                {output.has_more_content ? (
                  <div className={OUTPUT_ROW_CLASS_NAME}>
                    <IbTypography.Paragraph disabled strong type="secondary">
                      {translateElma365General('Read file: Next chunk offset')}
                    </IbTypography.Paragraph>
                    {output.next_chunk_offset}
                  </div>
                ) : null}
                <IbTypography.Paragraph disabled strong type="secondary">
                  {translateElma365General('Read file: Content')}
                </IbTypography.Paragraph>
                <MarkdownContent>{output.content as string}</MarkdownContent>
              </div>
            );
          }
          case ELMA365DescribeImageToolCallDataToolTypeEnum.DescribeImage: {
            const output = toolCallData.output as Record<string, unknown>;
            if (output?.result) {
              return <MarkdownContent>{output.result as string}</MarkdownContent>;
            }
            break;
          }
        }
        break;
      }
    }
    return <MarkdownContent>{agentMessageEventDataToMarkdown(toolCallData.output, isRawMode)}</MarkdownContent>;
  };

  return (
    <IbCollapse
      accordion={false}
      className={MAIN_CLASS_NAME}
      defaultActiveKey={['input', 'output']}
      expandIcon={({ isActive }) => <IbIcon iconName={isActive ? 'down' : 'up'} size={20} />}
      expandIconPosition="right"
    >
      {showRenderModeSwitcher(toolCallData.metadata) && (
        <Switcher
          options={[
            { value: DetailsRenderMode.RAW, label: <IbIcon iconName="code-brackets" size={16} /> },
            {
              value: DetailsRenderMode.STRUCTURED,
              label: <IbIcon iconName="list-view" size={16} />,
            },
          ]}
          value={renderMode}
          onChange={onRenderModeChange}
        />
      )}
      <IbCollapsePanel
        key="input"
        className={INPUT_PANEL_CLASS_NAME}
        header={
          <IbTypography.Paragraph strong type="secondary">
            {translate('Input')}
          </IbTypography.Paragraph>
        }
      >
        {renderInput()}
      </IbCollapsePanel>
      {toolCallData.output && (
        <IbCollapsePanel
          key="output"
          className={OUTPUT_PANEL_CLASS_NAME}
          header={
            <IbTypography.Paragraph strong type="secondary">
              {translate('Output')}
            </IbTypography.Paragraph>
          }
        >
          {renderOutput()}
        </IbCollapsePanel>
      )}
    </IbCollapse>
  );
};

export default ToolCallEventDetails;
